/**
 * Pagination — page-cursor control with ellipsis-elided page numbers.
 *
 * Renders a horizontally-laid row of: a "previous" button, a sequence
 * of numbered page buttons (with ellipses elision for long ranges), and
 * a "next" button. The component is fully controlled — the host owns
 * the current page and total count.
 *
 * Single responsibility: render the control. Computing the sliced
 * dataset and handling the underlying fetch is the consumer's job.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo } from "react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface PaginationProps {
  /** Current page (1-based). */
  readonly page: number;
  /** Total number of pages. */
  readonly pageCount: number;
  /** Page-change handler. */
  readonly onPageChange: (page: number) => void;
  /**
   * Maximum number of numbered buttons shown to either side of the
   * current page before the range collapses with an ellipsis.
   * Defaults to 1 (i.e. the current page plus one neighbour on each
   * side, so the rendered window is 3 buttons wide).
   */
  readonly siblingCount?: number;
  /** Accessible label for the surrounding `<nav>`. Defaults to "Pagination". */
  readonly ariaLabel?: string;
  /** Additional className appended to the outer `<nav>`. */
  readonly className?: string;
}

type PageEntry = number | "ellipsis";

interface NormalizedPaginationInput {
  readonly page: number;
  readonly pageCount: number;
  readonly siblingCount: number;
}

/**
 * Clamp the caller-supplied triple so a host that shrinks its dataset
 * (or passes a partially-stale snapshot) still renders a sensible
 * control instead of a degenerate ellipsis window. `pageCount <= 0`
 * collapses to an inert single-button strip.
 */
function normalizeInput(
  page: number,
  pageCount: number,
  siblingCount: number,
): NormalizedPaginationInput {
  const safePageCount = Math.max(0, Math.trunc(pageCount));
  const safeSiblingCount = Math.max(0, Math.trunc(siblingCount));
  if (safePageCount === 0) {
    return { page: 1, pageCount: 0, siblingCount: safeSiblingCount };
  }
  const safePage = Math.min(safePageCount, Math.max(1, Math.trunc(page)));
  return { page: safePage, pageCount: safePageCount, siblingCount: safeSiblingCount };
}

function buildPageEntries(
  page: number,
  pageCount: number,
  siblingCount: number,
): readonly PageEntry[] {
  if (pageCount <= 1) return [1];
  const totalNumbers = siblingCount * 2 + 5; // first, last, current, 2 ellipses
  if (pageCount <= totalNumbers) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const leftSibling = Math.max(2, page - siblingCount);
  const rightSibling = Math.min(pageCount - 1, page + siblingCount);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < pageCount - 1;

  const entries: PageEntry[] = [1];
  if (showLeftEllipsis) {
    entries.push("ellipsis");
  } else {
    for (let p = 2; p < leftSibling; p += 1) entries.push(p);
  }
  for (let p = leftSibling; p <= rightSibling; p += 1) entries.push(p);
  if (showRightEllipsis) {
    entries.push("ellipsis");
  } else {
    for (let p = rightSibling + 1; p < pageCount; p += 1) entries.push(p);
  }
  entries.push(pageCount);
  return entries;
}

const BUTTON_BASE: React.CSSProperties = {
  background: "white",
  border: `1px solid ${GRAY[300]}`,
  borderRadius: "0.375rem",
  color: GRAY[700],
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "0.8125rem",
  minWidth: "2.25rem",
  padding: "0.3rem 0.6rem",
};

const BUTTON_ACTIVE: React.CSSProperties = {
  background: PRIMARY[600],
  border: `1px solid ${PRIMARY[700]}`,
  color: "white",
};

const BUTTON_DISABLED: React.CSSProperties = {
  cursor: "not-allowed",
  opacity: 0.45,
};

const BUTTON_ACTIVE_STYLE: React.CSSProperties = { ...BUTTON_BASE, ...BUTTON_ACTIVE };
const BUTTON_DISABLED_STYLE: React.CSSProperties = { ...BUTTON_BASE, ...BUTTON_DISABLED };
const LIST_STYLE: React.CSSProperties = {
  display: "inline-flex",
  gap: "0.3rem",
  listStyle: "none",
  margin: 0,
  padding: 0,
};
const ELLIPSIS_STYLE: React.CSSProperties = { color: GRAY[500], padding: "0.3rem 0.4rem" };

/** Renders an accessible numbered pagination control. */
export const Pagination = React.memo(function Pagination({
  page,
  pageCount,
  onPageChange,
  siblingCount = 1,
  ariaLabel = "Pagination",
  className,
}: PaginationProps): React.JSX.Element {
  const normalized = useMemo(
    () => normalizeInput(page, pageCount, siblingCount),
    [page, pageCount, siblingCount],
  );
  const entries = useMemo(
    () => buildPageEntries(normalized.page, normalized.pageCount, normalized.siblingCount),
    [normalized.page, normalized.pageCount, normalized.siblingCount],
  );
  const canPrev = normalized.page > 1;
  const canNext = normalized.page < normalized.pageCount;
  const handlePrev = useCallback(() => {
    onPageChange(normalized.page - 1);
  }, [normalized.page, onPageChange]);
  const handleNext = useCallback(() => {
    onPageChange(normalized.page + 1);
  }, [normalized.page, onPageChange]);
  const handlePage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(Number(event.currentTarget.dataset["page"]));
    },
    [onPageChange],
  );

  return (
    <nav aria-label={ariaLabel} data-csk-pagination className={cn("font-body", className)}>
      <ul style={LIST_STYLE}>
        <li>
          <button
            type="button"
            aria-label="Previous page"
            disabled={!canPrev}
            onClick={handlePrev}
            data-csk-pagination-prev
            style={canPrev ? BUTTON_BASE : BUTTON_DISABLED_STYLE}
          >
            ‹
          </button>
        </li>
        {entries.map((entry, index) =>
          entry === "ellipsis" ? (
            <li key={`ellipsis-${index}`} aria-hidden="true">
              <span data-csk-pagination-ellipsis style={ELLIPSIS_STYLE}>
                …
              </span>
            </li>
          ) : (
            <li key={entry}>
              <button
                type="button"
                aria-current={entry === normalized.page ? "page" : undefined}
                aria-label={`Page ${entry}`}
                onClick={handlePage}
                data-page={entry}
                data-csk-pagination-page={entry}
                data-csk-pagination-active={entry === normalized.page ? "true" : "false"}
                style={entry === normalized.page ? BUTTON_ACTIVE_STYLE : BUTTON_BASE}
              >
                {entry}
              </button>
            </li>
          ),
        )}
        <li>
          <button
            type="button"
            aria-label="Next page"
            disabled={!canNext}
            onClick={handleNext}
            data-csk-pagination-next
            style={canNext ? BUTTON_BASE : BUTTON_DISABLED_STYLE}
          >
            ›
          </button>
        </li>
      </ul>
    </nav>
  );
});
